import { useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, CircleNotch, X, ImageSquare } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 10;

export default function PhotoAttach({ photos, setPhotos, testid = "photo" }) {
  const fileRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX - photos.length);
    e.target.value = "";
    for (const file of files) {
      if (!file.type.startsWith("image/")) { toast.error(`${file.name} is not an image.`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is over 10MB.`); continue; }
      const id = `${Date.now()}-${Math.random()}`;
      setPhotos((p) => [...p, { id, previewUrl: URL.createObjectURL(file), uploading: true }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => p.map((ph) => (ph.id === id ? { ...ph, uploading: false, path: data.path, url: data.url, filename: data.filename } : ph)));
      } catch {
        toast.error(`Couldn't upload ${file.name}.`);
        setPhotos((p) => p.filter((ph) => ph.id !== id));
      }
    }
  };

  return (
    <div>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" data-testid={`${testid}-button`} disabled={photos.length >= MAX} onClick={() => fileRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 border border-dashed border-[#46A01E] text-[#15306E] bg-[#F4FAF0] hover:bg-[#EAF4E3] px-4 py-3 rounded-sm text-sm font-semibold transition-colors disabled:opacity-50">
        <Camera size={18} weight="duotone" className="text-[#46A01E]" /> Add photos of your junk (optional, up to {MAX})
      </button>
      {photos.length > 0 && (
        <>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-3" data-testid={`${testid}-previews`}>
            {photos.map((ph) => (
              <div key={ph.id} className="relative aspect-square rounded-sm overflow-hidden border border-[#E4E4E7]">
                <img src={ph.previewUrl} alt={ph.filename || "upload"} className="w-full h-full object-cover" />
                {ph.uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><CircleNotch size={20} className="animate-spin text-white" /></div>}
                <button type="button" aria-label="Remove photo" data-testid={`${testid}-remove-${ph.id}`} onClick={() => setPhotos((p) => p.filter((x) => x.id !== ph.id))}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-[#46A01E] text-white rounded-full p-0.5 transition-colors"><X size={13} weight="bold" /></button>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#52525B] mt-2 flex items-center gap-1"><ImageSquare size={13} weight="fill" className="text-[#46A01E]" /> {photos.length} photo{photos.length > 1 ? "s" : ""} attached — helps us confirm faster.</p>
        </>
      )}
    </div>
  );
}
